import React, { useState, useEffect, useRef } from 'react';
import { usersApi, companiesApi } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Plus, MagnifyingGlass, Pencil, User, UploadSimple, CaretLeft, CaretRight, Trash, Eye, QrCode, Key } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { WorkerQrDialog } from '../../components/WorkerQrDialog';
import BulkUploadDialog from '../../components/BulkUploadDialog';
import DeleteConfirmDialog from '../../components/DeleteConfirmDialog';
import BulkDeleteConfirmDialog from '../../components/BulkDeleteConfirmDialog';
import { SingleDuplicateDialog } from '../../components/DuplicateConfirmDialog';
import { validateRut } from '../../utils/rutValidator';
import { Checkbox } from '../../components/ui/checkbox';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const requestSeq = useRef(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [companyFilter, setCompanyFilter] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const { impersonate } = useAuth();
  const navigate = useNavigate();
  const ITEMS_PER_PAGE = 10;
  const [editingUser, setEditingUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [resettingUser, setResettingUser] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [createDualRole, setCreateDualRole] = useState(false);
  const [duplicateUser, setDuplicateUser] = useState(null);
  const [emailDup, setEmailDup] = useState(null);
  const [pendingSubmitData, setPendingSubmitData] = useState(null);
  const [qrUser, setQrUser] = useState(null);
  const [formData, setFormData] = useState({
    rut: '',
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    password: '',
    role: 'worker',
    company_id: ''
  });

  // Load companies once for dropdowns
  useEffect(() => {
    companiesApi.getAll({ page_size: 999 }).then(res => {
      setCompanies(res.data.items || res.data);
    }).catch(() => {});
  }, []);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(searchTerm), 200);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Fetch users from server (paginated)
  useEffect(() => {
    fetchData();
  }, [currentPage, searchDebounced, roleFilter, companyFilter]);

  const fetchData = async () => {
    const seq = ++requestSeq.current;
    setLoading(true);
    try {
      const params = { page: currentPage, page_size: ITEMS_PER_PAGE };
      if (searchDebounced) params.search = searchDebounced;
      if (roleFilter !== 'all') params.role = roleFilter;
      if (companyFilter !== 'all' && companyFilter !== 'none') {
        // Find company id by name
        const comp = companies.find(c => c.name === companyFilter);
        if (comp) params.company_id = comp.id;
      }
      const res = await usersApi.getAll(params);
      if (seq !== requestSeq.current) return;
      const data = res.data;
      setUsers(data.items || data);
      setTotalPages(data.total_pages || 1);
      setTotalItems(data.total || 0);
    } catch (error) {
      if (seq === requestSeq.current) toast.error('Error al cargar datos');
    } finally {
      if (seq === requestSeq.current) { setLoading(false); setInitialLoaded(true); }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.rut) {
      const rutCheck = validateRut(formData.rut);
      if (!rutCheck.valid) { toast.error(rutCheck.error); return; }
      formData.rut = rutCheck.formatted;
    }
    const submitData = { ...formData };
    if (!editingUser && !submitData.password) {
      submitData.password = submitData.rut ? submitData.rut.replace(/[^0-9]/g, '').slice(0, 5) : '12345';
    }
    if (editingUser) {
      try {
        await usersApi.update(editingUser.id, submitData);
        if (createDualRole && (submitData.role === 'contractor' || submitData.role === 'worker')) {
          const dualRole = submitData.role === 'contractor' ? 'worker' : 'contractor';
          const dualData = { ...submitData, password: submitData.rut ? submitData.rut.replace(/[^0-9]/g, '').slice(0, 5) : '12345', role: dualRole };
          try {
            const checkRes = await usersApi.checkDuplicate({ email: submitData.email, role: dualRole, rut: submitData.rut });
            if (checkRes.data.exists) {
              await usersApi.update(checkRes.data.user.id, dualData);
              toast.success('Usuario actualizado y rol adicional actualizado');
            } else {
              await usersApi.create(dualData);
              toast.success('Usuario actualizado y rol adicional creado');
            }
          } catch { toast.success('Usuario actualizado. No se pudo crear el rol adicional.'); }
        } else {
          toast.success('Usuario actualizado');
        }
        setIsDialogOpen(false); resetForm(); fetchData();
      } catch (error) { toast.error(error.response?.data?.detail || 'Error al guardar'); }
      return;
    }
    // Check for duplicates before creating
    try {
      const checkRes = await usersApi.checkDuplicate({ email: submitData.email, role: submitData.role, rut: submitData.rut, company_id: submitData.company_id });
      if (checkRes.data.exists) {
        if (checkRes.data.email_same_company) {
          setEmailDup({ ...checkRes.data.user, email: submitData.email });
          return;
        }
        setDuplicateUser(checkRes.data.user);
        setPendingSubmitData(submitData);
        return;
      }
      await doCreate(submitData);
    } catch (error) { toast.error(error.response?.data?.detail || 'Error al verificar'); }
  };

  const doCreate = async (submitData) => {
    try {
      await usersApi.create(submitData);
      if (createDualRole && (submitData.role === 'contractor' || submitData.role === 'worker')) {
        try { await usersApi.create({ ...submitData, role: submitData.role === 'contractor' ? 'worker' : 'contractor' }); toast.success('Usuarios creados (Contratista + Trabajador)'); } catch { toast.success('Usuario creado. No se pudo crear el rol adicional.'); }
      } else { toast.success('Usuario creado'); }
      setIsDialogOpen(false); resetForm(); fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Error al crear';
      if (detail.includes('este correo en esta empresa')) {
        setEmailDup({ email: submitData.email });
      } else {
        toast.error(detail);
      }
    }
  };

  const handleUpdateDuplicate = async () => {
    if (!duplicateUser || !pendingSubmitData) return;
    try {
      await usersApi.update(duplicateUser.id, pendingSubmitData);
      toast.success('Usuario actualizado exitosamente');
      setDuplicateUser(null); setPendingSubmitData(null);
      setIsDialogOpen(false); resetForm(); fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Error al actualizar'); }
  };

  const resetForm = () => {
    setFormData({
      rut: '',
      first_name: '',
      last_name: '',
      phone: '',
      email: '',
      password: '',
      role: 'worker',
      company_id: ''
    });
    setEditingUser(null);
    setCreateDualRole(false);
  };

  const openEditDialog = (user) => {
    setEditingUser(user);
    setFormData({
      rut: user.rut || '',
      first_name: user.first_name,
      last_name: user.last_name,
      phone: user.phone || '',
      email: user.email,
      password: '',
      role: user.role,
      company_id: user.company_id || ''
    });
    setIsDialogOpen(true);
  };

  const handleResetPassword = async () => {
    if (!resettingUser) return;
    setResetting(true);
    try {
      const res = await usersApi.resetPassword(resettingUser.id);
      const temp = res.data?.temporary_password;
      toast.success(`Clave restablecida. Clave provisoria: ${temp}`, { duration: 8000 });
      setResettingUser(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al restablecer la clave');
    } finally {
      setResetting(false);
    }
  };

  // Build unique company names for filter from loaded companies
  const companyNames = companies.map(c => c.name).sort();

  // Reset page when filters change
  useEffect(() => { setCurrentPage(1); }, [searchDebounced, roleFilter, companyFilter]);

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const pageIds = users.map(u => u.id);
    const allSelected = pageIds.every(id => selectedIds.has(id));
    setSelectedIds(prev => {
      const next = new Set(prev);
      pageIds.forEach(id => allSelected ? next.delete(id) : next.add(id));
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const getRoleBadge = (role) => {
    const config = {
      admin: { label: 'Administrador', className: 'bg-purple-500/10 text-purple-400 border-purple-500/20' },
      contractor: { label: 'Contratista', className: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
      worker: { label: 'Trabajador', className: 'bg-green-500/10 text-green-400 border-green-500/20' },
      client: { label: 'Mandante', className: 'bg-orange-500/10 text-orange-400 border-orange-500/20' },
      relator: { label: 'Relator', className: 'bg-teal-500/10 text-teal-400 border-teal-500/20' },
    };
    const c = config[role] || config.worker;
    return <Badge className={`${c.className} border`}>{c.label}</Badge>;
  };

  if (loading && !initialLoaded) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2BBAA4]" />
      </div>
    );
  }

  const userRutCheck = validateRut(formData.rut);
  const userRutInvalid = formData.rut.trim() !== '' && !userRutCheck.valid;
  const userRequiredFilled = formData.rut.trim() && formData.first_name.trim() && formData.last_name.trim() && formData.email.trim() && formData.phone.trim() && formData.role && (formData.role === 'admin' || formData.company_id);
  const userCanSubmit = Boolean(userRequiredFilled) && userRutCheck.valid;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#2B4B8C]">Usuarios</h1>
          <p className="text-[#2B4B8C]/60">Gestiona los usuarios de la plataforma</p>
        </div>
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            onClick={() => setIsBulkUploadOpen(true)} 
            className="border-[#2B4B8C]/20 text-[#2B4B8C] hover:bg-[#2B4B8C]/5"
            data-testid="bulk-upload-users-btn"
          >
            <UploadSimple className="mr-2" size={20} />
            Carga Masiva
          </Button>
          <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#2BBAA4] text-black hover:bg-[#239986]" data-testid="create-user-btn">
              <Plus className="mr-2" size={20} />
              Nuevo Usuario
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white border-[#2B4B8C]/10 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-[#2B4B8C]">
                {editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-[#2B4B8C]/80">RUT</Label>
                <Input
                  value={formData.rut}
                  onChange={(e) => setFormData({ ...formData, rut: e.target.value })}
                  className={`bg-[#f8fafc] text-[#2B4B8C] ${userRutInvalid ? 'border-red-400 focus-visible:ring-red-400' : 'border-[#2B4B8C]/10'}`}
                  placeholder="12.345.678-9"
                  required
                  data-testid="user-rut"
                />
                {userRutInvalid && <p className="text-xs text-red-500" data-testid="user-rut-error">{userRutCheck.error}</p>}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#2B4B8C]/80">Nombre</Label>
                  <Input
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#2B4B8C]/80">Apellido</Label>
                  <Input
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]"
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#2B4B8C]/80">Teléfono</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#2B4B8C]/80">Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]"
                    required
                  />
                </div>
              </div>
              {!editingUser && (
                <div className="space-y-2">
                  <Label className="text-[#2B4B8C]/80">Clave</Label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]"
                    placeholder="Por defecto: primeros 5 dígitos del RUT"
                  />
                  <p className="text-xs text-[#2B4B8C]/40">Si se deja vacío, la clave será los primeros 5 dígitos del RUT</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#2B4B8C]/80">Rol</Label>
                  <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value, company_id: value === 'admin' ? '' : formData.company_id })}>
                    <SelectTrigger className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-[#2B4B8C]/10">
                      <SelectItem value="admin">Administrador</SelectItem>
                      <SelectItem value="contractor">Contratista</SelectItem>
                      <SelectItem value="worker">Trabajador</SelectItem>
                      <SelectItem value="client">Mandante</SelectItem>
                      <SelectItem value="relator">Relator</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#2B4B8C]/80">Empresa</Label>
                  <Select value={formData.company_id} onValueChange={(value) => setFormData({ ...formData, company_id: value })} disabled={formData.role === 'admin'}>
                    <SelectTrigger className={`bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C] ${formData.role === 'admin' ? 'opacity-50 cursor-not-allowed' : ''}`}>
                      <SelectValue placeholder="Seleccionar empresa" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-[#2B4B8C]/10">
                      {companies.map((company) => (
                        <SelectItem key={company.id} value={company.id}>{company.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Dual role checkbox */}
              {(formData.role === 'contractor' || formData.role === 'worker') && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-[#2BBAA4]/5 border border-[#2BBAA4]/20">
                  <Checkbox
                    id="dual-role"
                    checked={createDualRole}
                    onCheckedChange={setCreateDualRole}
                    data-testid="create-dual-role-checkbox"
                  />
                  <Label htmlFor="dual-role" className="text-sm text-[#2B4B8C] cursor-pointer leading-tight">
                    {formData.role === 'contractor'
                      ? 'Crear tambien como Trabajador'
                      : 'Crear tambien como Contratista'}
                  </Label>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="flex-1 border-[#2B4B8C]/10">
                  Cancelar
                </Button>
                <Button type="submit" disabled={!userCanSubmit} className="flex-1 bg-[#2BBAA4] text-black hover:bg-[#239986]">
                  {editingUser ? 'Actualizar' : 'Crear'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Bulk Upload Dialog - Users */}
      <BulkUploadDialog
        open={isBulkUploadOpen}
        onOpenChange={setIsBulkUploadOpen}
        title="Carga Masiva de Usuarios"
        templateHeaders={['Rut', 'Nombre', 'Apellidos', 'Teléfono', 'Email', 'Rol']}
        fieldNames={['rut', 'first_name', 'last_name', 'phone', 'email', 'role']}
        templateExample={['12.345.678-9', 'Juan', 'Pérez', '+56912345678', 'usuario@email.com', 'worker']}
        onUpload={async (data) => {
          const payload = { users: data.map(row => ({
            rut: row.rut || null,
            first_name: row.first_name,
            last_name: row.last_name,
            phone: row.phone || null,
            email: row.email,
            password: row.rut ? row.rut.replace(/[^0-9]/g, '').slice(0, 5) : '12345',
            role: row.role || 'worker',
            company_id: row.company_id || null
          }))};
          const res = await usersApi.bulkUpload(payload);
          fetchData();
          return res.data;
        }}
        entityName="usuarios"
      />

      {/* Filters */}
      <Card className="bg-white border-[#2B4B8C]/10">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-[#2B4B8C]/60" size={20} />
              <Input
                placeholder="Buscar por nombre, email o RUT..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]"
              />
            </div>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-full sm:w-48 bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]" data-testid="role-filter-select">
                <SelectValue placeholder="Filtrar por rol" />
              </SelectTrigger>
              <SelectContent className="bg-white border-[#2B4B8C]/10">
                <SelectItem value="all">Todos los roles</SelectItem>
                <SelectItem value="admin">Administrador</SelectItem>
                <SelectItem value="contractor">Contratista</SelectItem>
                <SelectItem value="worker">Trabajador</SelectItem>
                <SelectItem value="client">Mandante</SelectItem>
                <SelectItem value="relator">Relator</SelectItem>
              </SelectContent>
            </Select>
            <Select value={companyFilter} onValueChange={setCompanyFilter}>
              <SelectTrigger className="w-full sm:w-52 bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]" data-testid="company-filter-select">
                <SelectValue placeholder="Filtrar por empresa" />
              </SelectTrigger>
              <SelectContent className="bg-white border-[#2B4B8C]/10">
                <SelectItem value="all">Todas las empresas</SelectItem>
                <SelectItem value="none">Sin empresa</SelectItem>
                {companyNames.map((name, idx) => (
                  <SelectItem key={`comp-${idx}`} value={name}>{name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Delete Bar */}
      {selectedIds.size > 0 && (
        <div className="flex items-center justify-between p-3 rounded-lg bg-red-50 border border-red-200 animate-fade-in" data-testid="bulk-delete-bar">
          <span className="text-sm font-medium text-red-700">
            {selectedIds.size} usuario(s) seleccionado(s)
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={clearSelection} className="border-red-200 text-red-600 hover:bg-red-50 h-8" data-testid="clear-selection-btn">
              Deseleccionar
            </Button>
            <Button size="sm" onClick={() => setShowBulkDelete(true)} className="bg-red-500 text-white hover:bg-red-600 h-8" data-testid="bulk-delete-btn">
              <Trash size={14} className="mr-1" /> Eliminar Seleccionados
            </Button>
          </div>
        </div>
      )}

      {/* Users Table */}
      <Card className="bg-white border-[#2B4B8C]/10">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-[#2B4B8C]/10 hover:bg-transparent">
                <TableHead className="w-10">
                  <Checkbox
                    checked={users.length > 0 && users.every(u => selectedIds.has(u.id))}
                    onCheckedChange={toggleSelectAll}
                    data-testid="select-all-users-checkbox"
                  />
                </TableHead>
                <TableHead className="text-[#2B4B8C]/60">RUT</TableHead>
                <TableHead className="text-[#2B4B8C]/60">Nombre</TableHead>
                <TableHead className="text-[#2B4B8C]/60">Apellido</TableHead>
                <TableHead className="text-[#2B4B8C]/60">Fono</TableHead>
                <TableHead className="text-[#2B4B8C]/60">Mail</TableHead>
                <TableHead className="text-[#2B4B8C]/60">Rol</TableHead>
                <TableHead className="text-[#2B4B8C]/60">Empresa</TableHead>
                <TableHead className="text-[#2B4B8C]/60">Estado</TableHead>
                <TableHead className="text-[#2B4B8C]/60 text-center">QR</TableHead>
                <TableHead className="text-[#2B4B8C]/60 text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id} className={`border-[#2B4B8C]/5 hover:bg-[#f8fafc] ${selectedIds.has(user.id) ? 'bg-red-50/50' : ''}`}>
                  <TableCell>
                    <Checkbox
                      checked={selectedIds.has(user.id)}
                      onCheckedChange={() => toggleSelect(user.id)}
                      data-testid={`select-user-${user.id}`}
                    />
                  </TableCell>
                  <TableCell className="text-[#2B4B8C]/60">{user.rut || '-'}</TableCell>
                  <TableCell className="text-[#2B4B8C] font-medium">{user.first_name}</TableCell>
                  <TableCell className="text-[#2B4B8C]">{user.last_name}</TableCell>
                  <TableCell className="text-[#2B4B8C]/60">{user.phone || '-'}</TableCell>
                  <TableCell className="text-[#2B4B8C]/60">{user.email}</TableCell>
                  <TableCell>{getRoleBadge(user.role)}</TableCell>
                  <TableCell className="text-[#2B4B8C]/60">{user.company_name || '-'}</TableCell>
                  <TableCell>
                    <Badge className={user.is_active ? 'bg-green-500/10 text-green-400 border-green-500/20 border' : 'bg-red-500/10 text-red-400 border-red-500/20 border'}>
                      {user.is_active ? 'Activo' : 'Inactivo'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-center">
                    {(user.role === 'worker' || user.role === 'contractor') && (
                      <Button variant="ghost" size="sm" onClick={() => setQrUser(user)} className="text-[#2B4B8C]/40 hover:text-[#2BBAA4]" title="QR Competencias" data-testid={`qr-user-${user.id}`}>
                        <QrCode size={18} />
                      </Button>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={async () => {
                          try {
                            const userData = await impersonate(user.id);
                            const roleRoutes = { admin: '/admin', contractor: '/contractor', worker: '/worker', client: '/client', relator: '/relator' };
                            navigate(roleRoutes[userData.role] || '/');
                          } catch { toast.error('Error al impersonar usuario'); }
                        }}
                        className="text-amber-500/60 hover:text-amber-500"
                        title="Ver como este usuario"
                        data-testid={`impersonate-user-${user.id}`}
                      >
                        <Eye size={16} />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openEditDialog(user)} className="text-[#2B4B8C]/60 hover:text-[#2B4B8C]" data-testid={`edit-user-${user.id}`}>
                        <Pencil size={16} />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setResettingUser(user)} className="text-blue-400 hover:text-blue-500" title="Restablecer clave" data-testid={`reset-password-${user.id}`}>
                        <Key size={16} />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setDeletingUser(user)} className="text-red-400 hover:text-red-500" data-testid={`delete-user-${user.id}`}>
                        <Trash size={16} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {users.length === 0 && !loading && (
            <div className="text-center py-12 text-[#2B4B8C]/50">
              No se encontraron usuarios
            </div>
          )}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-[#2B4B8C]/10">
              <span className="text-sm text-[#2B4B8C]/60">
                Mostrando {(currentPage - 1) * ITEMS_PER_PAGE + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, totalItems)} de {totalItems}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(p => p - 1)}
                  className="border-[#2B4B8C]/10 text-[#2B4B8C]"
                  data-testid="users-prev-page"
                >
                  <CaretLeft size={16} className="mr-1" /> Anterior
                </Button>
                <span className="text-sm text-[#2B4B8C] font-medium px-2">
                  {currentPage} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(p => p + 1)}
                  className="border-[#2B4B8C]/10 text-[#2B4B8C]"
                  data-testid="users-next-page"
                >
                  Siguiente <CaretRight size={16} className="ml-1" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <DeleteConfirmDialog
        open={!!deletingUser}
        onOpenChange={(open) => { if (!open) setDeletingUser(null); }}
        entityName="Usuario"
        itemName={deletingUser ? `${deletingUser.first_name} ${deletingUser.last_name}` : ''}
        fetchDependencies={deletingUser ? () => usersApi.getDependencies(deletingUser.id) : null}
        onConfirmDelete={async () => {
          await usersApi.delete(deletingUser.id);
          toast.success('Usuario eliminado');
          setDeletingUser(null);
          fetchData();
        }}
      />

      <Dialog open={!!resettingUser} onOpenChange={(open) => { if (!open) setResettingUser(null); }}>
        <DialogContent className="bg-white border-[#2B4B8C]/10 max-w-md" data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#2B4B8C] flex items-center gap-2"><Key size={18} className="text-blue-500" /> Restablecer clave</DialogTitle>
          </DialogHeader>
          {resettingUser && (
            <div className="space-y-3">
              <p className="text-sm text-[#2B4B8C]/70">
                Se restablecerá la clave de <span className="font-semibold text-[#2B4B8C]">{resettingUser.first_name} {resettingUser.last_name}</span> (RUT {resettingUser.rut}).
              </p>
              <div className="rounded-lg bg-blue-50 border border-blue-200 px-3 py-2 text-sm text-blue-800">
                La clave provisoria serán los <b>5 primeros dígitos del RUT</b>: <span className="font-mono font-bold" data-testid="reset-password-preview">{(resettingUser.rut || '').replace(/\D/g, '').slice(0, 5)}</span>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setResettingUser(null)} data-testid="reset-password-cancel">Cancelar</Button>
                <Button onClick={handleResetPassword} disabled={resetting} className="bg-blue-500 text-white hover:bg-blue-600" data-testid="reset-password-confirm">
                  {resetting ? 'Restableciendo...' : 'Restablecer clave'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <BulkDeleteConfirmDialog
        open={showBulkDelete}
        onOpenChange={setShowBulkDelete}
        entityName="usuario(s)"
        count={selectedIds.size}
        fetchDependencies={() => usersApi.bulkDependencies([...selectedIds])}
        onConfirmDelete={async () => {
          await usersApi.bulkDelete([...selectedIds]);
          toast.success(`${selectedIds.size} usuario(s) eliminado(s)`);
          clearSelection();
          fetchData();
        }}
      />

      <Dialog open={!!emailDup} onOpenChange={(open) => { if (!open) setEmailDup(null); }}>
        <DialogContent className="bg-white border-red-200 max-w-md" data-testid="email-duplicate-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">Correo repetido</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-3 text-sm text-red-800">
              El correo <span className="font-semibold">{emailDup?.email}</span> ya está registrado para un trabajador en esta empresa. No se puede repetir dentro de la misma empresa.
            </div>
            <p className="text-xs text-[#2B4B8C]/60">
              Puedes usar este correo solo si el trabajador pertenece a <b>otra empresa</b>, o corrige el correo para continuar.
            </p>
            <div className="flex justify-end pt-1">
              <Button onClick={() => setEmailDup(null)} className="bg-[#2BBAA4] text-black hover:bg-[#239986]" data-testid="email-duplicate-close">Entendido</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <SingleDuplicateDialog
        open={!!duplicateUser}
        onOpenChange={(open) => { if (!open) { setDuplicateUser(null); setPendingSubmitData(null); } }}
        existingUser={duplicateUser}
        onUpdate={handleUpdateDuplicate}
        onCancel={() => { setDuplicateUser(null); setPendingSubmitData(null); }}
      />

      <WorkerQrDialog user={qrUser} onClose={() => setQrUser(null)} />
    </div>
  );
}
